<script lang="ts">
  import { onMount } from 'svelte';

  import {
    Button,
    DataTable,
    InlineNotification,
    Link,
    NotificationActionButton,
    Select,
    SelectItem,
    Toolbar,
    ToolbarContent,
    TooltipIcon,
  } from 'carbon-components-svelte';

  import EditIcon from 'carbon-icons-svelte/lib/Edit16';
  import FilterIcon from 'carbon-icons-svelte/lib/Filter16';
  import InfoIcon from 'carbon-icons-svelte/lib/Information16';
  import { useNavigate } from 'svelte-navigator';

  import type { DataTableHeader, DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';

  import AddSlaTaskModal from './AddSlaTaskModal.svelte';
  import EditSlaTaskAssociationsModal from './EditSlaTaskAssociationsModal.svelte';
  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import ToolbarSearchWithRouting from '../../../components/ToolbarSearchWithRouting.svelte';
  import PaginationWithRouting from '../../../components/PaginationWithRouting.svelte';
  import StatusIcon from '../../../components/StatusIcon.svelte';
  import DoubleTag from '../../../components/DoubleTag.svelte';

  import { getSLATasks } from '../../../services';
  import { rowContainsText } from '../../../utils';

  import type { SlaTaskRow } from '../../../../shared/types/sla-task-types';

  type SlaTaskModalCloseDetail = { action: string; data: { id: string; friendlyName: string } };

  const navigate = useNavigate();

  const tableHeaders: DataTableHeader[] = [
    { key: 'friendlyName', value: 'Task Name' },
    { key: 'internalOnly', value: 'Internal Only' },
    { key: 'active', value: 'Active' },
    { key: 'schedule.time', value: 'Time' },
    { key: 'associations', value: 'Associations' },
  ];

  let pageData: { loading: boolean; tasks: SlaTaskRow[]; error: unknown } = { loading: true, tasks: [], error: null };
  let query = { currentPage: 1, pageSize: 25, searchText: '', selectedFeedId: '' };

  let modals: {
    addSlaTask: { open: boolean };
    editSlaTaskAssociations: { open: boolean; data?: SlaTaskRow };
  } = {
    addSlaTask: { open: false },
    editSlaTaskAssociations: { open: false },
  };

  async function fetchTasks(): Promise<void> {
    pageData = { ...pageData, loading: true, error: null };

    try {
      const tasks = await getSLATasks();
      pageData = { ...pageData, tasks, loading: false };
    } catch (error) {
      console.error('[Sla] Failed to load SLA tasks!', error);
      pageData = { ...pageData, loading: false, error };
    }
  }

  function updatePage(detail: SlaTaskModalCloseDetail): void {
    const { action } = detail;

    switch (action) {
      case 'add-task':
      case 'edit-associations':
        fetchTasks();
        break;
      default:
        console.warn(`Unknown action "${action}"! No handler implemented.`, { detail });
    }
  }

  function onSearchTextChange(event: CustomEvent<string>): void {
    query = { ...query, currentPage: 1, searchText: event?.detail ?? '' };
  }

  function onFeedFilterChange(event: CustomEvent<string>): void {
    query = { ...query, currentPage: 1, selectedFeedId: event.detail ?? '' };
  }

  function viewSlaDetails(event: Event, slaTaskId: string): void {
    event.preventDefault();
    navigate(`/sla/${slaTaskId}`);
  }

  function openModal(name: keyof typeof modals, forwardData: { data?: DataTableRow } = {}) {
    const data = forwardData.data as SlaTaskRow | undefined;

    return function handleOpen(): void {
      modals = {
        ...modals,
        [name]: { open: true, ...(data ? { data } : {}) },
      };
    };
  }

  function closeModal(name: keyof typeof modals) {
    return function handleClose(event: CustomEvent<SlaTaskModalCloseDetail | undefined>): void {
      const { detail } = event;

      if (detail) {
        updatePage(detail);
      }

      modals = {
        ...modals,
        [name]: { open: false },
      };
    };
  }

  onMount(fetchTasks);

  $: availableFeedFilters = (() => {
    const optionsById = new Map<string, string>();

    pageData.tasks.forEach((task) => {
      (task.associations ?? []).forEach((association) => {
        if (!association.feedId) {
          return;
        }

        const label = association.feed?.name ?? association.feedId;

        if (!optionsById.has(association.feedId)) {
          optionsById.set(association.feedId, label);
        }
      });
    });

    return Array.from(optionsById.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  })();

  $: if (query.selectedFeedId && !availableFeedFilters.some((option) => option.id === query.selectedFeedId)) {
    query = { ...query, currentPage: 1, selectedFeedId: '' };
  }

  $: filteredTasks = pageData.tasks.filter((taskRow) => {
    if (query.selectedFeedId && !(taskRow.associations ?? []).some((association) => association.feedId === query.selectedFeedId)) {
      return false;
    }

    if (!query.searchText) {
      return true;
    }

    return rowContainsText(taskRow, query.searchText);
  });
</script>

{#if pageData.error}
  <InlineNotification
    hideCloseButton
    kind="error"
    lowContrast
    title="Error:"
    subtitle="Failed to load SLA tasks! Please try again later."
  />
{/if}

{#if !pageData.loading && !pageData.error && pageData.tasks.length === 0}
  <InlineNotification hideCloseButton kind="info" lowContrast title="Info:" subtitle="No SLA tasks available yet.">
    <svelte:fragment slot="actions">
      <NotificationActionButton on:click={openModal('addSlaTask')}>Create first</NotificationActionButton>
    </svelte:fragment>
  </InlineNotification>
{:else}
  <LoadingSpinner loading={pageData.loading}>
    <div class="table">
      <DataTable headers={tableHeaders} page={query.currentPage} pageSize={query.pageSize} rows={filteredTasks ?? []} sortable>
        <span slot="cell" let:cell let:row>
          {#if cell.key === 'internalOnly' || cell.key === 'active'}
            <StatusIcon valid={cell.value} />
          {:else if cell.key === 'schedule.time'}
            <DoubleTag label={row.schedule.timezone} value={cell.value} withCopyButton={false} />
          {:else if cell.key === 'associations'}
            <div class="link-button">
              <Link icon={EditIcon} on:click={openModal('editSlaTaskAssociations', { data: row })}>
                {row.associations.length}
              </Link>
            </div>
          {:else if cell.key === 'friendlyName'}
            <div class="name-cell">
              <Link href={`/sla/${row.id}`} on:click={(event) => viewSlaDetails(event, row.id)}>{cell.value}</Link>
              {#if row.description}
                <TooltipIcon direction="right" tooltipText={row.description} icon={InfoIcon} portalTooltip />
              {/if}
            </div>
          {:else}
            {cell.value || '—'}
          {/if}
        </span>
        <Toolbar>
          <ToolbarContent>
            <div class="table-filters">
              <FilterIcon />
              <Select inline selected={query.selectedFeedId} on:update={onFeedFilterChange}>
                <SelectItem value="" text="All feeds" />
                {#each availableFeedFilters as option (option.id)}
                  <SelectItem value={option.id} text={option.label} />
                {/each}
              </Select>
            </div>
            <div class="search">
              <ToolbarSearchWithRouting
                placeholder="Search tasks"
                persistent
                bind:searchText={query.searchText}
                on:toolbarSearchValueChange={onSearchTextChange}
              />
              <Button on:click={openModal('addSlaTask')}>Add task</Button>
            </div>
          </ToolbarContent>
        </Toolbar>
      </DataTable>
      <PaginationWithRouting
        pageSizes={[10, 25, 50]}
        totalItems={filteredTasks?.length ?? 0}
        bind:page={query.currentPage}
        bind:pageSize={query.pageSize}
      />
    </div>
  </LoadingSpinner>
{/if}

{#if modals.addSlaTask.open}
  <AddSlaTaskModal open on:close={closeModal('addSlaTask')} />
{/if}

{#if modals.editSlaTaskAssociations.open}
  <EditSlaTaskAssociationsModal open data={modals.editSlaTaskAssociations.data ?? null} on:close={closeModal('editSlaTaskAssociations')} />
{/if}

<style>
  .table-filters {
    align-items: center;
    display: flex;
    padding: 0 1rem;
  }

  .table-filters :global(.bx--select--inline .bx--select-input) {
    max-width: 150px;
  }

  .table-filters :global(.bx--select--inline) {
    margin-right: unset !important;
  }

  .name-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .link-button {
    cursor: pointer;
  }

  .search {
    display: flex;
    width: 100%;
  }

  @media (max-width: 42rem) {
    .table :global(.bx--toolbar-content) {
      flex-direction: column;
      gap: 0;
      height: 6rem;
    }

    .table-filters {
      flex: 1 1 100%;
    }
  }
</style>
